// Lay every variant out in 3D, once, into GPU-ready buffers.
//
// ── WHY THIS SHAPE ───────────────────────────────────────────────────────────
// Not decoration. Two facts about real nuclei drive it:
//
//  1. Chromosome territories. Interphase chromosomes are not a tangled soup —
//     each one occupies its own broadly separate region of the nucleus
//     (Cremer & Cremer, 2001). So each chromosome gets its own volume here,
//     seeded on a Fibonacci sphere for even coverage.
//  2. Chromatin is a coiled polymer. DNA winds onto nucleosomes, which coil
//     into a ~30 nm fibre, which loops again. A helical coil per chromosome is
//     a fair cartoon of that, and it has the useful property that genomic
//     neighbours stay spatial neighbours — so a dense run of markers reads as
//     a dense patch rather than being scattered.
//
// What it is NOT: a real 3D genome structure. Actual conformations come from
// Hi-C, vary between cell types and between individuals, and nothing in a
// consumer genotype file constrains them. Positions here are derived from
// genomic coordinates alone.

import type { ParsedGenome } from '../types';
import { BUILD_CHROM_LENGTHS, type GenomeBuild } from './cytobands';
import { CONDITIONS } from './conditions';

export interface NucleusLayout {
  /** xyz triples, one per plotted variant. */
  positions: Float32Array;
  /** rgb triples in 0..1, parallel to `positions`. */
  colors: Float32Array;
  /** Per-point size multiplier — catalog markers are drawn larger. */
  sizes: Float32Array;
  /** Index into genome.variants for each plotted point, for picking. */
  sourceIndex: Uint32Array;
  count: number;
  /** Variants with no chromosome we could place (unplaced contigs). */
  skipped: number;
  /** Radius that comfortably contains everything, for framing the camera. */
  extent: number;
  perChrom: { chrom: string; count: number; color: [number, number, number] }[];
  /**
   * Faint guide geometry tracing each chromosome's coil, as gl.LINES pairs.
   *
   * Without it the view only reads as a shape when the file is dense. An array
   * export puts a few hundred points on each chromosome — far too sparse for a
   * coil to be visible from the points alone — so the backbone carries the
   * structure and the points carry the data.
   */
  backbone: Float32Array;
  backboneColors: Float32Array;
  backboneCount: number;
}

const CHROM_ORDER_24 = [
  '1','2','3','4','5','6','7','8','9','10','11','12',
  '13','14','15','16','17','18','19','20','21','22','X','Y','MT',
];

/** MT is a 16.6 kb circle and has no cytoband entry; it still deserves a dot. */
const MT_LENGTH = 16569;

/**
 * Distinct hues around the wheel. Adjacent chromosomes get well-separated hues
 * so neighbouring territories never blend into one another.
 */
function chromColor(i: number): [number, number, number] {
  // Golden-angle stepping keeps successive hues far apart.
  const h = (i * 137.508) % 360;
  const s = 0.62;
  const l = i % 2 === 0 ? 0.62 : 0.5;
  return hslToRgb(h / 360, s, l);
}

function hslToRgb(h: number, s: number, l: number): [number, number, number] {
  const k = (n: number) => (n + h * 12) % 12;
  const a = s * Math.min(l, 1 - l);
  const f = (n: number) => l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
  return [f(0), f(8), f(4)];
}

/** Evenly spaced directions on a sphere — no clustering at the poles. */
function fibonacciDirection(i: number, n: number): [number, number, number] {
  const golden = Math.PI * (3 - Math.sqrt(5));
  const y = 1 - (i / Math.max(1, n - 1)) * 2; // 1 .. -1
  const r = Math.sqrt(Math.max(0, 1 - y * y));
  const theta = golden * i;
  return [Math.cos(theta) * r, y, Math.sin(theta) * r];
}

/** Any unit vector perpendicular to `v`, chosen without hitting a degenerate case. */
function perpendicular(v: [number, number, number]): [number, number, number] {
  const [x, y, z] = v;
  // Cross with whichever axis is least aligned, so the result is never near zero.
  const ax = Math.abs(x), ay = Math.abs(y), az = Math.abs(z);
  let ox = 0, oy = 0, oz = 0;
  if (ax <= ay && ax <= az) ox = 1;
  else if (ay <= az) oy = 1;
  else oz = 1;
  const cx = y * oz - z * oy;
  const cy = z * ox - x * oz;
  const cz = x * oy - y * ox;
  const len = Math.hypot(cx, cy, cz) || 1;
  return [cx / len, cy / len, cz / len];
}

export interface LayoutOptions {
  build: GenomeBuild;
  /** Radius of the nucleus the coils radiate into. */
  radius?: number;
  /** Coil turns per chromosome — higher looks more tightly wound. */
  turns?: number;
  /** Radius of each chromosome's coil. */
  coilRadius?: number;
}

export function layoutNucleus(
  genome: ParsedGenome,
  opts: LayoutOptions,
): NucleusLayout {
  const { build, radius = 6, turns = 11, coilRadius = 0.62 } = opts;

  const lengths: Record<string, number> = {
    ...BUILD_CHROM_LENGTHS[build],
    MT: MT_LENGTH,
  };

  const catalogRs = new Set<string>();
  for (const c of CONDITIONS) for (const m of c.markers) catalogRs.add(m.rsid.toLowerCase());

  const order = new Map(CHROM_ORDER_24.map((c, i) => [c, i]));
  const total = CHROM_ORDER_24.length;

  // Precompute each chromosome's frame: an outward axis plus two perpendiculars
  // spanning the plane the coil winds in.
  const frames = new Map<
    string,
    { axis: [number, number, number]; u: [number, number, number]; v: [number, number, number]; span: number; base: number }
  >();
  const maxLen = Math.max(...CHROM_ORDER_24.map((c) => lengths[c] ?? 0));
  for (let i = 0; i < total; i++) {
    const chrom = CHROM_ORDER_24[i];
    const len = lengths[chrom] ?? 0;
    if (!len) continue;
    const axis = fibonacciDirection(i, total);
    const u = perpendicular(axis);
    // v = axis x u, completing a right-handed frame.
    const v: [number, number, number] = [
      axis[1] * u[2] - axis[2] * u[1],
      axis[2] * u[0] - axis[0] * u[2],
      axis[0] * u[1] - axis[1] * u[0],
    ];
    // Coil length scales with the square root of chromosome length, so chr1
    // doesn't dwarf chr21 to the point the small ones vanish.
    const span = radius * (0.35 + 0.65 * Math.sqrt(len / maxLen));
    frames.set(chrom, { axis, u, v, span, base: radius * 0.22 });
  }

  /** Point on a chromosome's coil at fraction t along it. */
  const coilAt = (
    frame: { axis: number[]; u: number[]; v: number[]; span: number; base: number },
    t: number,
  ): [number, number, number] => {
    const angle = t * turns * Math.PI * 2;
    const ca = Math.cos(angle);
    const sa = Math.sin(angle);
    const along = frame.base + t * frame.span;
    return [
      frame.axis[0] * along + (frame.u[0] * ca + frame.v[0] * sa) * coilRadius,
      frame.axis[1] * along + (frame.u[1] * ca + frame.v[1] * sa) * coilRadius,
      frame.axis[2] * along + (frame.u[2] * ca + frame.v[2] * sa) * coilRadius,
    ];
  };

  // Backbone: sample each coil finely and emit gl.LINES pairs.
  const SEGMENTS = 320;
  const bbVerts: number[] = [];
  const bbCols: number[] = [];
  for (const [chrom, frame] of frames) {
    const [cr, cg, cb] = chromColor(order.get(chrom) ?? 0);
    let prev = coilAt(frame, 0);
    for (let sIdx = 1; sIdx <= SEGMENTS; sIdx++) {
      const cur = coilAt(frame, sIdx / SEGMENTS);
      bbVerts.push(prev[0], prev[1], prev[2], cur[0], cur[1], cur[2]);
      // Dim: the backbone is scaffolding, the points are the data.
      for (let q = 0; q < 2; q++) bbCols.push(cr * 0.3, cg * 0.3, cb * 0.3);
      prev = cur;
    }
  }

  const n = genome.variants.length;
  const positions = new Float32Array(n * 3);
  const colors = new Float32Array(n * 3);
  const sizes = new Float32Array(n);
  const sourceIndex = new Uint32Array(n);
  const perChromCount = new Map<string, number>();

  let k = 0;
  let skipped = 0;
  let extent = 0;

  for (let i = 0; i < n; i++) {
    const variant = genome.variants[i];
    const len = lengths[variant.chrom];
    const frame = frames.get(variant.chrom);
    if (!len || !frame) {
      skipped++;
      continue;
    }

    // t along the chromosome, clamped so an out-of-range coordinate (wrong
    // assembly) lands at the tip rather than flying off into space.
    const t = Math.min(1, Math.max(0, variant.pos / len));
    const [x, y, z] = coilAt(frame, t);

    positions[k * 3] = x;
    positions[k * 3 + 1] = y;
    positions[k * 3 + 2] = z;

    const ci = order.get(variant.chrom) ?? 0;
    const isMarker = catalogRs.has(variant.rsid.toLowerCase());
    if (isMarker) {
      // Near-white so catalog markers read as highlights against every hue.
      colors[k * 3] = 1;
      colors[k * 3 + 1] = 0.97;
      colors[k * 3 + 2] = 0.85;
      sizes[k] = 3.2;
    } else {
      const [r, g, b] = chromColor(ci);
      colors[k * 3] = r;
      colors[k * 3 + 1] = g;
      colors[k * 3 + 2] = b;
      sizes[k] = 1;
    }

    sourceIndex[k] = i;
    perChromCount.set(variant.chrom, (perChromCount.get(variant.chrom) ?? 0) + 1);
    extent = Math.max(extent, Math.hypot(x, y, z));
    k++;
  }

  const perChrom = CHROM_ORDER_24.filter((c) => perChromCount.has(c)).map((chrom) => ({
    chrom,
    count: perChromCount.get(chrom) ?? 0,
    color: chromColor(order.get(chrom) ?? 0),
  }));

  return {
    positions: positions.subarray(0, k * 3),
    colors: colors.subarray(0, k * 3),
    sizes: sizes.subarray(0, k),
    sourceIndex: sourceIndex.subarray(0, k),
    count: k,
    skipped,
    extent: extent || radius,
    perChrom,
    backbone: new Float32Array(bbVerts),
    backboneColors: new Float32Array(bbCols),
    backboneCount: bbVerts.length / 3,
  };
}
